import React, { useMemo, useRef, useEffect, useState } from "react"
import { useInView } from "react-intersection-observer"
import { Box, Skeleton, Stack } from "@mui/material"
import { Container } from ".."

// Add type declaration for Trustpilot global
declare global {
  interface Window {
    Trustpilot?: {
      loadFromElement: (element: HTMLElement | null, refresh?: boolean) => void
    }
  }
}

type WidgetProps = {
  id?: string
  variant: "hero" | "preFooter" | "quotes"
}

const variantConfig = {
  hero: {
    sku: "547cf0db00006400057be14e",
    templateId: "5406e65db0d04a09e042d5fc",
    theme: "dark",
    style: "center",
    scrollToreviews: false,
    minReviewsHeight: 24,
    width: "100%",
    locale: "en-US",
  },
  preFooter: {
    sku: "547cf0db00006400057be14e",
    templateId: "53aa8912dec7e10d38f59f36",
    theme: "light",
    stars: "5",
    scrollToreviews: false,
    minReviewsHeight: 140,
    width: "100%",
    locale: "en-US",
  },
  quotes: {
    sku: "547cf0db00006400057be14e",
    templateId: "53aa8912dec7e10d38f59f36",
    theme: "light",
    stars: "5",
    scrollToreviews: false,
    minReviewsHeight: 140,
    width: "100%",
    locale: "en-US",
  },
}

// SSR-safe skeleton component
const TrustPilotSkeleton: React.FC<{ variant: WidgetProps['variant'] }> = ({ variant }) => {
  const config = variantConfig[variant]

  if (variant === "hero") {
    return (
      <Box
        sx={{
          minHeight: config.minReviewsHeight,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(0, 0, 0, 0.1)',
          borderRadius: 1,
          padding: 2
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Skeleton variant="text" width={80} height={20} sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)' }} />
          <Stack direction="row" spacing={0.5}>
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} variant="circular" width={16} height={16} sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)' }} />
            ))}
          </Stack>
          <Skeleton variant="text" width={120} height={20} sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)' }} />
        </Stack>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        minHeight: config.minReviewsHeight,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        padding: 3,
        backgroundColor: 'rgba(0, 0, 0, 0.02)',
        borderRadius: 1
      }}
    >
      <Stack spacing={2}>
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="center">
          <Skeleton variant="text" width={100} height={24} />
          <Stack direction="row" spacing={0.5}>
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} variant="circular" width={20} height={20} />
            ))}
          </Stack>
          <Skeleton variant="text" width={80} height={24} />
        </Stack>
        <Stack spacing={1}>
          <Skeleton variant="text" width="90%" height={20} />
          <Skeleton variant="text" width="75%" height={20} />
          <Skeleton variant="text" width="60%" height={20} />
        </Stack>
      </Stack>
    </Box>
  )
}

const TrustPilotWidget: React.FC<WidgetProps> = ({ id, variant }) => {
  const [isMounted, setIsMounted] = useState(false)
  const [isScriptLoaded, setIsScriptLoaded] = useState(false)
  const [shouldLoad, setShouldLoad] = useState(false)
  const [showSkeleton, setShowSkeleton] = useState(true)
  const widgetRef = useRef<HTMLDivElement>(null)

  // For hero variant, load immediately. For others, use intersection observer
  const shouldUseIntersectionObserver = variant !== "hero"

  const { ref: inViewRef, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
    rootMargin: "50px 0px",
    skip: !shouldUseIntersectionObserver
  })

  const config = variantConfig[variant]

  // Combine refs
  const setRefs = React.useCallback((element: HTMLDivElement | null) => {
    widgetRef.current = element
    if (shouldUseIntersectionObserver) {
      inViewRef(element)
    }
  }, [inViewRef, shouldUseIntersectionObserver])

  // Preload TrustPilot script on component mount (SSR-safe)
  useEffect(() => {
    setIsMounted(true)

    // Preload script immediately for better performance
    const link = document.createElement('link')
    link.rel = 'preload'
    link.href = '//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js'
    link.as = 'script'
    link.crossOrigin = 'anonymous'
    document.head.appendChild(link)

    // For hero variant, start loading immediately
    if (variant === "hero") {
      setShouldLoad(true)
    }

    return () => {
      document.head.removeChild(link)
    }
  }, [variant])

  // Load script and initialize widget when conditions are met
  useEffect(() => {
    if (!isMounted) return

    const shouldStartLoading = variant === "hero" || (shouldUseIntersectionObserver && inView)

    if (shouldStartLoading && !shouldLoad) {
      setShouldLoad(true)
    }
  }, [isMounted, inView, shouldLoad, variant, shouldUseIntersectionObserver])

  // Load TrustPilot script
  useEffect(() => {
    if (!isMounted || !shouldLoad || isScriptLoaded) return

    const loadTrustPilotScript = () => {
      // Check if script already exists
      const existingScript = document.querySelector('script[src*="widget.trustpilot.com"]')
      if (existingScript) {
        setIsScriptLoaded(true)
        if (window.Trustpilot && widgetRef.current) {
          window.Trustpilot.loadFromElement(widgetRef.current, true)
          // Hide skeleton after a short delay
          setTimeout(() => setShowSkeleton(false), 500)
        }
        return
      }

      const script = document.createElement('script')
      script.src = '//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js'
      script.async = true
      script.defer = true

      script.onload = () => {
        setIsScriptLoaded(true)
        if (window.Trustpilot && widgetRef.current) {
          window.Trustpilot.loadFromElement(widgetRef.current, true)
          // Hide skeleton after widget loads
          setTimeout(() => setShowSkeleton(false), 1000)
        }
      }

      script.onerror = () => {
        console.warn('TrustPilot script failed to load')
        setShowSkeleton(false)
      }

      document.head.appendChild(script)
    }

    // Load script immediately for hero, with slight delay for others
    if (variant === "hero") {
      loadTrustPilotScript()
    } else {
      setTimeout(loadTrustPilotScript, 100)
    }
  }, [isMounted, shouldLoad, isScriptLoaded, variant])

  // Re-initialize widget when script is loaded
  useEffect(() => {
    if (isMounted && isScriptLoaded && shouldLoad && widgetRef.current && window.Trustpilot) {
      window.Trustpilot.loadFromElement(widgetRef.current, true)
    }
  }, [isMounted, isScriptLoaded, shouldLoad])

  const widget = useMemo(() => {
    // During SSR, always render skeleton
    if (!isMounted) {
      return <TrustPilotSkeleton variant={variant} />
    }

    // For non-hero variants, always render a container that can be observed
    // even when showing skeleton
    if (!shouldLoad && shouldUseIntersectionObserver) {
      return (
        <Box ref={setRefs}>
          <TrustPilotSkeleton variant={variant} />
        </Box>
      )
    }

    // Show skeleton while loading for hero variant
    if (!shouldLoad) {
      return <TrustPilotSkeleton variant={variant} />
    }

    // Always render the actual widget element once shouldLoad is true
    // The skeleton will be overlaid on top until showSkeleton is false
    if (variant === "hero") {
      const heroConfig = variantConfig.hero
      return (
        <Box position="relative">
          <div
            ref={setRefs}
            className="trustpilot-widget"
            data-locale={heroConfig.locale}
            data-template-id={heroConfig.templateId}
            data-businessunit-id={heroConfig.sku}
            data-style-height={`${heroConfig.minReviewsHeight}px`}
            data-style-width={heroConfig.width}
            data-theme={heroConfig.theme}
            data-style-alignment={heroConfig.style}
            style={{ opacity: showSkeleton ? 0 : 1, transition: 'opacity 0.3s ease' }}
          />
          {showSkeleton && (
            <Box position="absolute" top={0} left={0} right={0} bottom={0}>
              <TrustPilotSkeleton variant={variant} />
            </Box>
          )}
        </Box>
      )
    }

    if (variant === "preFooter") {
      const preFooterConfig = variantConfig.preFooter
      return (
        <Box position="relative">
          <div
            ref={setRefs}
            className="trustpilot-widget"
            data-locale={preFooterConfig.locale}
            data-template-id={preFooterConfig.templateId}
            data-businessunit-id={preFooterConfig.sku}
            data-style-height={`${preFooterConfig.minReviewsHeight}px`}
            data-style-width={preFooterConfig.width}
            data-theme={preFooterConfig.theme}
            data-stars={preFooterConfig.stars}
            data-review-languages="en"
            style={{ opacity: showSkeleton ? 0 : 1, transition: 'opacity 0.3s ease' }}
          />
          {showSkeleton && (
            <Box position="absolute" top={0} left={0} right={0} bottom={0}>
              <TrustPilotSkeleton variant={variant} />
            </Box>
          )}
        </Box>
      )
    }

    // Fallback for quotes variant
    const quotesConfig = variantConfig.quotes
    return (
      <Box position="relative">
        <div
          ref={setRefs}
          className="trustpilot-widget"
          data-locale={quotesConfig.locale}
          data-template-id={quotesConfig.templateId}
          data-businessunit-id={quotesConfig.sku}
          data-style-height={`${quotesConfig.minReviewsHeight}px`}
          data-style-width={quotesConfig.width}
          data-theme={quotesConfig.theme}
          data-stars={quotesConfig.stars}
          data-review-languages="en"
          style={{ opacity: showSkeleton ? 0 : 1, transition: 'opacity 0.3s ease' }}
        />
        {showSkeleton && (
          <Box position="absolute" top={0} left={0} right={0} bottom={0}>
            <TrustPilotSkeleton variant={variant} />
          </Box>
        )}
      </Box>
    )
  }, [isMounted, shouldLoad, showSkeleton, variant, setRefs])

  if (variant === "hero") {
    return <Box>{widget}</Box>
  }

  if (variant === "preFooter") {
    return (
      <Container maxWidth={1300} sx={{ marginBottom: '2rem' }}>
        {widget}
      </Container>
    )
  }

  return (
    <Box>
      <Box width="100%">{widget}</Box>
    </Box>
  )
}

export default TrustPilotWidget
